import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../App";
import moment from "moment/moment";
import api from "./api/api";

function DatePopUpV2() {
	const { cart, shop, editCart2, setPopup, lockEdit } = useContext(AppContext);
	const [orderDate, setOrderDate] = useState(
		cart.delivery_date
			? moment(cart.delivery_date, "YYYY-MM-DD HH:mm:ss").format("YYYY-MM-DD")
			: moment().format("YYYY-MM-DD")
	);
	const [activeService, setActiveService] = useState(() => {
		if (!cart?.delivery_date) return 1;
		const h = moment(cart.delivery_date, "YYYY-MM-DD HH:mm:ss").hour();
		return h >= 17 ? 2 : 1;
	});
	const [selectedCell, setSelectedCell] = useState(null);
	const [selectedMapSlot, setSelectedMapSlot] = useState(null);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [apiData, setApiData] = useState(null);
	const [noData, setNoData] = useState(false);
	const [manualDeselection, setManualDeselection] = useState(false);
	const [slotOrders, setSlotOrders] = useState([]);
	const [dragIndex, setDragIndex] = useState(null);
	const [dragOverIndex, setDragOverIndex] = useState(null);
	const [debugMode, setDebugMode] = useState(() => localStorage.getItem("dp2-debug-mode") === "1");
	const [headHeights, setHeadHeights] = useState({});
	const [expandedPastSlots, setExpandedPastSlots] = useState({});
	const [serviceChosenManually, setServiceChosenManually] = useState(false);

	const deliveryType = lockEdit ? "takeaway" : cart.delivery_type.id;
	const showMap = deliveryType !== "takeaway";

	const isTakeawayFlow = deliveryType === "takeaway";

	const dataView = useMemo(() => {
		if (!apiData) return emptyView();
		return buildViewFromApi(apiData, { takeawayOnly: isTakeawayFlow });
	}, [apiData, isTakeawayFlow]);
	const cartLoadInfo = useMemo(() => computeCartLoadInfo(cart), [cart]);
	const selectedSlotStart = selectedMapSlot?.dateTime || selectedCell?.dateTime || null;
	const isTakeawaySelection =
		Boolean(selectedCell) &&
		(selectedCell?.riderId === "free" || Number(selectedCell?.driverId || 0) === 0);
	const orderAddressLine = useMemo(() => {
		const address = [cart?.address?.address, cart?.address?.number].filter(Boolean).join(" ").trim();
		const city = String(cart?.address?.city || "").trim();
		if (address && city) return `${address}, ${city}`;
		return address || city || "Indirizzo non disponibile";
	}, [cart?.address?.address, cart?.address?.number, cart?.address?.city]);
	const selectedDebugSlot = useMemo(() => {
		if (!selectedSlotStart) return null;
		const sections = Array.isArray(apiData?.sections) ? apiData.sections : [];
		for (const section of sections) {
			for (const slot of section?.slots || []) {
				if (slot?.start === selectedSlotStart) {
					return {
						service: section?.service,
						sectionLabel: section?.label || `Servizio ${section?.service || ""}`.trim(),
						slot,
					};
				}
			}
		}
		return null;
	}, [apiData, selectedSlotStart]);
	const baseSlotOrders = useMemo(() => {
		if (!selectedCell?.dateTime) return [];
		const orders = Array.isArray(apiData?.map?.orders) ? apiData.map.orders : [];
		const slotRanges = buildSlotRanges(apiData?.sections || [], apiData?.meta?.slot_minutes || 15);
		const selectedDriverNorm = Number(selectedCell?.driverId || 0);
		const isFreeSelection = selectedCell?.riderId === "free" || selectedDriverNorm === 0;
		const list = orders
			.filter((o) => {
				const slot = getOrderSlot(o.delivery_date, slotRanges, apiData?.meta?.slot_minutes || 15);
				if (slot?.start !== selectedCell.dateTime) return false;
				if (isFreeSelection) return false;
				const orderDriverNorm = Number(o.driver_id || 0);
				return orderDriverNorm === selectedDriverNorm;
			})
			.map((o) => ({ ...o, isCurrent: false }))
			.sort((a, b) => {
				const ao = Number.isFinite(Number(a.delivery_order)) ? Number(a.delivery_order) : 9999;
				const bo = Number.isFinite(Number(b.delivery_order)) ? Number(b.delivery_order) : 9999;
				if (ao !== bo) return ao - bo;
				return Number(a.id || 0) - Number(b.id || 0);
			});
		const currentAddress = [cart?.address?.address, cart?.address?.number].filter(Boolean).join(" ").trim();
		const currentCity = (cart?.address?.city || "").trim();
		const currentRow = {
			id: cart?.order_id || "corrente",
			address: currentAddress || null,
			city: currentCity || null,
			isCurrent: true,
		};
		const isEditing = Boolean(cart?.order_id);
		const originalSlot = cart?.delivery_date ? moment(cart.delivery_date, "YYYY-MM-DD HH:mm:ss") : null;
		const selectedSlot = selectedCell?.dateTime ? moment(selectedCell.dateTime, "YYYY-MM-DD HH:mm:ss") : null;
		const sameOriginalSlot =
			isEditing && originalSlot?.isValid() && selectedSlot?.isValid()
				? originalSlot.isSame(selectedSlot, "minute")
				: false;
		const originalPosition = Number(cart?.delivery_order || 0);
		if (!isFreeSelection && sameOriginalSlot && originalPosition > 0) {
			const insertIndex = Math.min(Math.max(originalPosition - 1, 0), list.length);
			list.splice(insertIndex, 0, currentRow);
		} else if (!isFreeSelection) {
			// New slot selection behaves like a new order: append at the end.
			list.push(currentRow);
		}
		const shopName = apiData?.meta?.shop_name || "Negozio";
		const shopLabel = shopName;
		return [
			{ id: "shop-start", address: shopLabel, city: null, isShop: true, isCurrent: false },
			...list,
			{ id: "shop-end", address: shopLabel, city: null, isShop: true, isCurrent: false },
		];
	}, [apiData, selectedCell, cart?.address, cart?.order_id, cart?.delivery_order]);

	useEffect(() => {
		setSlotOrders(baseSlotOrders);
	}, [baseSlotOrders]);

	useEffect(() => {
		const onToggleDebug = () => {
			setDebugMode((prev) => {
				const next = !prev;
				localStorage.setItem("dp2-debug-mode", next ? "1" : "0");
				return next;
			});
		};
		window.addEventListener("dp2-toggle-debug", onToggleDebug);
		return () => window.removeEventListener("dp2-toggle-debug", onToggleDebug);
	}, []);

	useEffect(() => {
		let active = true;

		async function loadData() {
			setLoading(true);
			setLoadError("");
			setSelectedCell(null);
			setSelectedMapSlot(null);
			setNoData(false);

			const request = {
				shop_id: shop?.id,
				delivery_type: deliveryType,
				delivery_date: orderDate,
				service: activeService,
				cart_load: cartLoadInfo.oven,
				cart_backpack_load: cartLoadInfo.backpack,
			};
			if (cart?.order_id) request.order_id = Number(cart.order_id);
			if (deliveryType === "delivery") {
				request.coord = cart?.address?.coord || "";
			}

			if (!request.shop_id) {
				setApiData(null);
				setNoData(true);
				setLoadError("Negozio non disponibile");
				setLoading(false);
				return;
			}

			try {
				const hasConfiguredSections = (data) =>
					Array.isArray(data?.sections) && data.sections.some((s) => Array.isArray(s?.slots) && s.slots.length > 0);
				const hasFutureSlots = (data) =>
					Array.isArray(data?.sections) &&
					data.sections.some((s) =>
						(s?.slots || []).some((slot) => {
							const start = moment(slot?.start);
							return start.isValid() && !start.isBefore(moment(), "minute");
						}),
					);
				const isToday = moment(orderDate, "YYYY-MM-DD", true).isSame(moment(), "day");

				const res = await api.postProtected("/pos2/availables_delivery_times_v2/", request);
				if (!active) return;
				if (res.success === 1 && hasConfiguredSections(res.data)) {
					if (!serviceChosenManually && isToday && !hasFutureSlots(res.data)) {
						const fallbackService = Number(activeService) === 2 ? 1 : 2;
						const fallbackRequest = { ...request, service: fallbackService };
						const fallbackRes = await api.postProtected("/pos2/availables_delivery_times_v2/", fallbackRequest);
						if (!active) return;
						if (fallbackRes.success === 1 && hasConfiguredSections(fallbackRes.data) && hasFutureSlots(fallbackRes.data)) {
							setActiveService(fallbackService);
							setServiceChosenManually(false);
							setApiData(fallbackRes.data);
							setNoData(false);
							setLoadError("");
							return;
						}
					}

					setApiData(res.data);
					setNoData(false);
				} else if (res.success === 1) {
					if (!serviceChosenManually) {
						const fallbackService = Number(activeService) === 2 ? 1 : 2;
						const fallbackRequest = { ...request, service: fallbackService };
						const fallbackRes = await api.postProtected("/pos2/availables_delivery_times_v2/", fallbackRequest);
						if (!active) return;
						if (fallbackRes.success === 1 && hasConfiguredSections(fallbackRes.data)) {
							setActiveService(fallbackService);
							setServiceChosenManually(false);
							setApiData(fallbackRes.data);
							setNoData(false);
							setLoadError("");
							return;
						}
					}

					setApiData(res.data || null);
					setNoData(true);
					setLoadError(
						buildNoDataMessage(res.data || null, {
							orderDate,
							activeService,
							deliveryType,
						}),
					);
				} else {
					setApiData(null);
					setNoData(true);
					setLoadError(res.error || "Errore caricamento dati v2");
				}
			} catch (error) {
				if (!active) return;
				setApiData(null);
				setNoData(true);
				setLoadError("Errore caricamento dati v2");
			} finally {
				if (active) setLoading(false);
			}
		}

		loadData();
		return () => {
			active = false;
		};
	}, [orderDate, activeService, deliveryType, shop?.id, cart?.address?.coord, cartLoadInfo.oven, cartLoadInfo.backpack, serviceChosenManually]);

	useEffect(() => {
		if (manualDeselection) return;
		if (selectedCell) return;
		if (!apiData?.sections?.length) return;
		if (!cart?.delivery_date) return;

		const targetDriverId = isTakeawayFlow ? null : Number(cart?.driver_id || 0) || null;
		const targetDate = moment(cart.delivery_date, "YYYY-MM-DD HH:mm:ss");
		if (!targetDate.isValid()) return;

		const target = findCellForOrder(apiData.sections, targetDate, targetDriverId);
		if (!target) return;

		setSelectedCell(target);
	}, [apiData, cart?.delivery_date, cart?.driver_id, selectedCell, manualDeselection, isTakeawayFlow]);

	useEffect(() => {
		if (serviceChosenManually) return;
		if (!apiData?.sections?.length) return;
		if (!moment(orderDate, "YYYY-MM-DD", true).isSame(moment(), "day")) return;

		const now = moment();
		const currentSection = apiData.sections.find((s) => Number(s?.service) === Number(activeService));
		if (!currentSection) return;

		const currentHasFutureSlots = (currentSection.slots || []).some((slot) => {
			const slotStart = moment(slot?.start);
			return slotStart.isValid() && !slotStart.isBefore(now, "minute");
		});
		if (currentHasFutureSlots) return;

		const nextSection = apiData.sections.find((s) => {
			if (Number(s?.service) === Number(activeService)) return false;
			return (s?.slots || []).some((slot) => {
				const slotStart = moment(slot?.start);
				return slotStart.isValid() && !slotStart.isBefore(now, "minute");
			});
		});
		if (!nextSection) return;

		setActiveService(Number(nextSection.service));
		setServiceChosenManually(false);
		setSelectedCell(null);
		setSelectedMapSlot(null);
		setManualDeselection(false);
		setExpandedPastSlots({});
	}, [apiData, activeService, orderDate, serviceChosenManually]);

	useEffect(() => {
		if (serviceChosenManually) return;
		if (!apiData?.sections?.length) return;
		const sectionsWithSlots = apiData.sections.filter((s) => Array.isArray(s?.slots) && s.slots.length > 0);
		if (sectionsWithSlots.length === 0) return;

		const currentSection = sectionsWithSlots.find((s) => Number(s?.service) === Number(activeService));
		if (currentSection) return;

		const isToday = moment(orderDate, "YYYY-MM-DD", true).isSame(moment(), "day");
		let fallbackSection = null;
		if (isToday) {
			const now = moment();
			fallbackSection =
				sectionsWithSlots.find((s) =>
					(s.slots || []).some((slot) => {
						const slotStart = moment(slot?.start);
						return slotStart.isValid() && !slotStart.isBefore(now, "minute");
					})
				) || null;
		}
		if (!fallbackSection) fallbackSection = sectionsWithSlots[0];
		if (!fallbackSection) return;

		setActiveService(Number(fallbackSection.service));
		setServiceChosenManually(false);
		setSelectedCell(null);
		setSelectedMapSlot(null);
		setManualDeselection(false);
		setExpandedPastSlots({});
	}, [apiData, activeService, orderDate, serviceChosenManually]);

	const changeDay = (offset) => {
		setSelectedCell(null);
		setSelectedMapSlot(null);
		setManualDeselection(false);
		setServiceChosenManually(false);
		setExpandedPastSlots({});
		setOrderDate(moment(orderDate, "YYYY-MM-DD").add(offset, "day").format("YYYY-MM-DD"));
	};

	const changeService = (service) => {
		if (service === activeService) return;
		setServiceChosenManually(true);
		setActiveService(service);
		setSelectedCell(null);
		setSelectedMapSlot(null);
		setManualDeselection(false);
		setExpandedPastSlots({});
	};

	const getCellClass = (value, riderId, nearCapacity = false) => {
		if (riderId === "free") {
			const required = Number(cartLoadInfo.oven || 0);
			const remaining = Number(value || 0);
			if (required <= 0) return nearCapacity ? "warn" : "ok";
			if (remaining < required) return "danger";
			if (remaining < required * 1.3 || nearCapacity) return "warn";
			return "ok";
		}
		const requiredBackpack = Number(cartLoadInfo.backpack || 0);
		const remainingBackpack = Number(value || 0);
		if (requiredBackpack <= 0) return "ok";
		if (remainingBackpack < requiredBackpack) return "danger";
		if (remainingBackpack < requiredBackpack * 1.3) return "warn";
		return "ok";
	};

	const saveSelection = () => {
		if (!selectedCell) return;
		const selectedDriverNorm = Number(selectedCell?.driverId || 0);
		const isForcedTakeaway = deliveryType === "takeaway";
		const isFreeSelection = isForcedTakeaway || selectedCell?.riderId === "free" || selectedDriverNorm === 0;
		const nonShopRows = (slotOrders || []).filter((o) => !o.isShop);
		const currentOrderIndex = nonShopRows.findIndex((o) => o.isCurrent);
		const allOrders = Array.isArray(apiData?.map?.orders) ? apiData.map.orders : [];
		const slotRanges = buildSlotRanges(apiData?.sections || [], apiData?.meta?.slot_minutes || 15);
		const currentOrderId = Number(cart?.order_id || 0) || null;
		const slotOrdersCount = allOrders.filter((o) => {
			const slot = getOrderSlot(o.delivery_date, slotRanges, apiData?.meta?.slot_minutes || 15);
			if (slot?.start !== selectedCell.dateTime) return false;
			const orderDriverNorm = Number(o.driver_id || 0);
			if (!isFreeSelection && orderDriverNorm !== selectedDriverNorm) return false;
			const oid = Number(o.id || 0) || null;
			if (currentOrderId && oid === currentOrderId) return false;
			return true;
		}).length;
		const data = {
			delivery_date: selectedCell.dateTime,
			delivery_time_range: selectedCell.timeRange,
			driver_id: isFreeSelection ? null : selectedCell.driverId,
			delivery_order: isFreeSelection ? 1 : currentOrderIndex >= 0 ? currentOrderIndex + 1 : slotOrdersCount + 1,
			delivery_ordering: isFreeSelection
				? []
				: nonShopRows.map((o, idx) => ({
					id: o.isCurrent ? null : Number(o.id),
					current: o.isCurrent ? 1 : 0,
					delivery_order: idx + 1,
				})),
			delivery_type:
				isFreeSelection || isForcedTakeaway
					? { id: "takeaway", name: "Asporto" }
					: { id: "delivery", name: "Domicilio" },
		};
		editCart2(data);
		setPopup(false);
	};

	const moveSlotOrder = (fromIndex, toIndex) => {
		if (!Array.isArray(slotOrders)) return;
		if (fromIndex === toIndex) return;
		if (fromIndex < 0 || toIndex < 0) return;
		if (fromIndex >= slotOrders.length || toIndex >= slotOrders.length) return;
		const fromRow = slotOrders[fromIndex];
		if (!fromRow || fromRow.isShop) return;
		let targetIndex = toIndex;
		const targetRow = slotOrders[targetIndex];
		if (!targetRow) return;
		// Allow dropping on shop anchors by snapping to nearest movable row.
		if (targetRow.isShop) {
			if (targetIndex <= fromIndex) {
				const nextMovable = slotOrders.findIndex((row, idx) => idx > targetIndex && row && !row.isShop);
				if (nextMovable < 0) return;
				targetIndex = nextMovable;
			} else {
				let prevMovable = -1;
				for (let i = targetIndex - 1; i >= 0; i--) {
					if (slotOrders[i] && !slotOrders[i].isShop) {
						prevMovable = i;
						break;
					}
				}
				if (prevMovable < 0) return;
				targetIndex = prevMovable;
			}
		}
		if (fromIndex === targetIndex) return;
		const next = [...slotOrders];
		const [moved] = next.splice(fromIndex, 1);
		next.splice(targetIndex, 0, moved);
		setSlotOrders(next);
	};

	const updateSectionHeadHeight = (sectionKey, node) => {
		if (!node || !sectionKey) return;
		const next = Math.ceil(node.getBoundingClientRect().height || 0);
		if (!next) return;
		setHeadHeights((prev) => (prev?.[sectionKey] === next ? prev : { ...prev, [sectionKey]: next }));
	};

	const renderSection = (section) => {
		const slotColumns = section.slotItems
			.map((slot) => {
				const isCompactPast = slot.isPast && !expandedPastSlots[slot.dateTime];
				if (isCompactPast) return "minmax(56px, 56px)";
				return "minmax(104px, 104px)";
			})
			.join(" ");
		const rowStyle = {
			gridTemplateColumns: `130px ${slotColumns}`,
		};
		const visibleRiders = isTakeawayFlow ? section.riders.filter((r) => r.id === "free") : section.riders;
		return (
			<div className="dp2-section" key={section.key}>
				<div className="dp2-grid">
					<div
						className="dp2-grid-row dp2-grid-head"
						style={rowStyle}
						ref={(node) => updateSectionHeadHeight(section.key, node)}
					>
						<div className="dp2-grid-label">
							<div className="dp2-grid-service-toggle">
								<button
									className={`dp2-service-tab ${activeService === 1 ? "active" : ""}`}
									onClick={() => changeService(1)}
								>
									Pranzo
								</button>
								<button
									className={`dp2-service-tab ${activeService === 2 ? "active" : ""}`}
									onClick={() => changeService(2)}
								>
									Cena
								</button>
							</div>
						</div>
						{section.slotItems.map((slot) => {
							const isSlotSelected =
								selectedMapSlot &&
								selectedMapSlot.sectionKey === section.key &&
								selectedMapSlot.dateTime === slot.dateTime;
							return (
								<button
									key={`${slot.label}-${slot.dateTime}`}
									className={`dp2-grid-slot ${slot.isPast && !expandedPastSlots[slot.dateTime] ? "compact" : ""} ${isSlotSelected ? "selected" : ""} ${slot.warn ? "warn" : ""} ${slot.enabled ? "" : "disabled"}`}
									title={slot.enabled ? "" : slot.disableReason || "Slot non disponibile"}
									onClick={() => {
										if (slot.isPast) {
											setExpandedPastSlots((prev) => ({
												...prev,
												[slot.dateTime]: !prev[slot.dateTime],
											}));
											return;
										}
										if (isSlotSelected) {
											setSelectedMapSlot(null);
											return;
										}
										setSelectedMapSlot({
											sectionKey: section.key,
											slot: slot.label,
											dateTime: slot.dateTime,
										});
									}}
								>
									<span className="dp2-grid-slot-time">{slot.label}</span>
									{!isTakeawayFlow && !(slot.isPast && !expandedPastSlots[slot.dateTime]) && (
										<span className="dp2-grid-slot-meta">Consegne libere: {slot.availableDrivers}</span>
									)}
									{slot.statusLabels.length > 0 && !(slot.isPast && !expandedPastSlots[slot.dateTime]) && (
										<div className="dp2-grid-slot-alerts">
											{slot.statusLabels.map((label) => (
												<span className="dp2-grid-slot-alert" key={`${slot.dateTime}-${label}`}>
														{squashAlertLabel(label)}
													</span>
												))}
											</div>
										)}
									</button>
								);
							})}
						</div>
					{visibleRiders.map((rider) => (
						<div
							className={`dp2-grid-row ${rider.id === "free" ? "dp2-grid-row-free" : ""}`}
							key={rider.id}
							style={
								rider.id === "free"
									? { ...rowStyle, "--dp2-head-offset": `${headHeights?.[section.key] || 52}px` }
									: rowStyle
							}
						>
							<div className="dp2-grid-label">
								{rider.id === "free" ? (
									<div className="dp2-grid-label-free">
										<div>{rider.name}</div>
											<div className="dp2-grid-label-free-sub">Carico disponibile</div>
									</div>
								) : (
									rider.name
								)}
							</div>
							{section.slotItems.map((slot) => {
								const cell = section.cells[`${rider.id}-${slot.label}`];
								if (!cell) return <div key={`${rider.id}-${slot.label}`} className="dp2-cell-empty" />;
								const isCompactPast = slot.isPast && !expandedPastSlots[slot.dateTime];
								const isSelected =
									selectedCell &&
									selectedCell.sectionKey === section.key &&
									selectedCell.riderId === rider.id &&
									selectedCell.slot === slot.label;
								const maxOrdersPerDriver = Number(cell.maxOrdersPerDriverSlot);
								const availableOrders = Number(cell.availableOrders);
								const hasOrdersInfo =
									Number.isFinite(availableOrders) && Number.isFinite(maxOrdersPerDriver);
								const assignedOrders = hasOrdersInfo
									? Math.max(0, Math.min(maxOrdersPerDriver, maxOrdersPerDriver - availableOrders))
									: null;
								return (
									<button
										key={`${rider.id}-${slot.label}`}
										className={`dp2-cell ${getCellClass(rider.id === "free" ? cell.load : cell.backpack, rider.id, cell.freeNearCapacity)} ${isSelected ? "selected" : ""} ${cell.enabled ? "" : "disabled"}`}
										disabled={!cell.enabled}
										title={!cell.enabled ? cell.disabledReason || slot.disableReason || "Slot non disponibile" : ""}
										onClick={() => {
											if (slot.isPast && !expandedPastSlots[slot.dateTime]) {
												setExpandedPastSlots((prev) => ({
													...prev,
													[slot.dateTime]: true,
												}));
												return;
											}
											if (isSelected) {
												setSelectedCell(null);
												setManualDeselection(true);
												return;
											}
											setSelectedMapSlot(null);
											setManualDeselection(false);
											setSelectedCell({
												sectionKey: section.key,
												riderId: rider.id,
												driverId: cell.driverId,
												riderName: rider.name,
												slot: slot.label,
												dateTime: cell.dateTime,
												timeRange: cell.timeRange,
												load: cell.load,
												backpack: cell.backpack,
												oven: cell.oven,
											});
										}}
										>
											{rider.id !== "free" ? (
												isCompactPast ? (
													<span className="dp2-cell-main">{cell.backpack}</span>
												) : (
													<div className="dp2-cell-metrics">
														<div className="dp2-cell-metric">
															<span className="dp2-cell-metric-value">{cell.backpack}</span>
															<span className="dp2-cell-metric-label">zaino</span>
														</div>
														<span className="dp2-cell-metric-divider" />
														<div className="dp2-cell-metric">
															<span className="dp2-cell-metric-value">
																{hasOrdersInfo ? `${assignedOrders}/${maxOrdersPerDriver}` : "-"}
															</span>
															<span className="dp2-cell-metric-label">consegna</span>
														</div>
													</div>
												)
										) : (
											cell.load
										)}
									</button>
								);
							})}
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderEmptySection = (message) => {
		const rowStyle = { gridTemplateColumns: "130px minmax(240px, 1fr)" };
		return (
			<div className="dp2-section" key={`empty-${activeService}`}>
				<div className="dp2-grid">
					<div className="dp2-grid-row dp2-grid-head dp2-grid-head-empty" style={rowStyle}>
						<div className="dp2-grid-label">
							<div className="dp2-grid-service-toggle">
								<button
									className={`dp2-service-tab ${activeService === 1 ? "active" : ""}`}
									onClick={() => changeService(1)}
								>
									Pranzo
								</button>
								<button
									className={`dp2-service-tab ${activeService === 2 ? "active" : ""}`}
									onClick={() => changeService(2)}
								>
									Cena
								</button>
							</div>
						</div>
						<div className="dp2-grid-empty-message">{message || "Nessun orario configurato"}</div>
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className={`date-popup-v2 ${showMap ? "" : "no-map-mode"}`.trim()}>
				<div className={`dp2-layout ${showMap ? "" : "no-map"}`.trim()}>
					{showMap && (
						<div className="dp2-map">
						<button
							className="dp2-side-btn dp2-map-debug"
							onClick={() => window.dispatchEvent(new CustomEvent("dp2-toggle-debug"))}
						>
							Debug
						</button>
						<div className="dp2-map-filter">
							Filtro mappa orario: <strong>{selectedMapSlot ? selectedMapSlot.slot : "tutti"}</strong>
						</div>
						<div className="dp2-map-canvas">
							<DeliveryMap
							shopCoord={apiData?.map?.shop_coord}
							riders={apiData?.riders || []}
							orders={apiData?.map?.orders || []}
							currentOrderCoord={cart?.address?.coord || null}
							selectedSlotOrders={slotOrders}
							sections={apiData?.sections || []}
							slotMinutes={apiData?.meta?.slot_minutes || 15}
							selectedSlotStart={selectedSlotStart}
							selectedDriverId={selectedMapSlot ? "all" : selectedCell?.driverId || null}
							onOrderSelect={(selection) => {
								if (!selection) return;
								setSelectedMapSlot(null);
								setSelectedCell(selection);
							}}
							/>
						</div>
						</div>
					)}

				<div className="dp2-side">
					<div className="dp2-side-fixed">
						<div className="dp2-toolbar-row">
							<div className="dp2-toolbar">
								<button
									className="dp2-toolbar-nav"
									onClick={() => changeDay(-1)}
									aria-label="Giorno precedente"
								>
									&#8249;
								</button>
								<div className="dp2-date">{moment(orderDate, "YYYY-MM-DD").format("ddd DD MMM YYYY")}</div>
								<button
									className="dp2-toolbar-nav"
									onClick={() => changeDay(1)}
									aria-label="Giorno successivo"
								>
									&#8250;
								</button>
							</div>
							<button className="dp2-side-btn close" onClick={() => setPopup(false)} aria-label="Chiudi popup">
								&times;
							</button>
						</div>

						{(loading || !!loadError) && (
							<div className="dp2-info">
								{loading && <div className="dp2-msg">Caricamento dati...</div>}
								{!!loadError && <div className="dp2-msg error">{loadError}</div>}
							</div>
						)}

								<div className="dp2-selected">
									<div className="dp2-selected-address">
										Indirizzo: <strong>{orderAddressLine}</strong>
									</div>
									<div>
										Orario: <strong>{selectedCell ? selectedCell.slot : "-"}</strong>
									</div>
									<div>
										Forno: <strong>{cartLoadInfo.oven}</strong>
									</div>
									{isTakeawayFlow ? (
										<div className="dp2-selected-metrics">
											<span className="dp2-selected-type">
												Tipo: <strong>asporto</strong>
											</span>
										</div>
									) : (
										<div className="dp2-selected-metrics">
											<span>
												Rider: <strong>{selectedCell ? (isTakeawaySelection ? "-" : selectedCell.riderName) : "-"}</strong>
											</span>
											<span>
												Zaino: <strong>{isTakeawaySelection ? "-" : cartLoadInfo.backpack}</strong>
											</span>
											{isTakeawaySelection && (
												<span className="dp2-selected-type">
													Tipo: <strong>asporto</strong>
												</span>
											)}
										</div>
									)}
							</div>
					</div>

					<div className="dp2-side-scroll">
						{debugMode && selectedDebugSlot && (
							<div className="dp2-debug-box">
								<div className="dp2-debug-title">Debug slot</div>
								<div className="dp2-debug-row">
									Servizio: <strong>{selectedDebugSlot.sectionLabel}</strong>
								</div>
								<div className="dp2-debug-row">
									Slot: <strong>{moment(selectedDebugSlot.slot.start).format("DD/MM HH:mm")}</strong>
								</div>
								<div className="dp2-debug-row">
									Stato: <strong>{Number(selectedDebugSlot.slot.enabled) === 1 ? "abilitato" : "disabilitato"}</strong>
								</div>
								{Number(selectedDebugSlot.slot.enabled) !== 1 && (
									<div className="dp2-debug-row">
										Motivo: <strong>{buildSlotDisableReason(selectedDebugSlot.slot)}</strong>
									</div>
								)}
								<div className="dp2-debug-row">
									Forno h:{" "}
									<strong>
										{Number(selectedDebugSlot.slot.actual_load || 0)} /{" "}
										{Number(selectedDebugSlot.slot.max_load || 0)}
									</strong>
								</div>
								<div className="dp2-debug-row">
									Forno slot:{" "}
									<strong>
										{Number(selectedDebugSlot.slot.actual_load_slot || 0)} /{" "}
										{Number(selectedDebugSlot.slot.max_load_slot || 0)}
									</strong>
								</div>
								<div className="dp2-debug-row">
									Driver slot:{" "}
									<strong>
										{Number(selectedDebugSlot.slot.actual_load_drivers || 0)} /{" "}
										{Number(selectedDebugSlot.slot.max_load_drivers || 0)}
									</strong>
								</div>
								<div className="dp2-debug-row">
									Zaino slot:{" "}
									<strong>
										{Number(selectedDebugSlot.slot.actual_backpack_slot || 0)} /{" "}
										{Number(selectedDebugSlot.slot.max_backpack_slot || 0)}
									</strong>
								</div>
								<div className="dp2-debug-row">
									Incoming carrello:{" "}
									<strong>
										forno {Number(selectedDebugSlot.slot.incoming_load || 0)}, zaino{" "}
										{Number(selectedDebugSlot.slot.incoming_backpack_load || 0)}, driver{" "}
										{Number(selectedDebugSlot.slot.incoming_driver_load || 0)}
									</strong>
								</div>
								<div className="dp2-debug-row">
									Ordini/rider slot:{" "}
									<strong>{Number(selectedDebugSlot.slot.max_orders_per_driver_slot || 0)}</strong>
								</div>
								<div className="dp2-debug-riders">
									{(selectedDebugSlot.slot.cells || []).map((c, idx) => (
										<div className="dp2-debug-rider" key={`dbg-rider-${idx}-${c.rider_id || "free"}`}>
											<span>{c.rider_name || "Rider"}</span>
											<span>
												ordini {Number(c.orders_count || 0)}
												{Number.isFinite(Number(c.max_orders_per_driver_slot))
													? `/${Number(c.max_orders_per_driver_slot)}`
													: ""}
											</span>
											{Number.isFinite(Number(c.available_orders)) && (
												<span>liberi {Number(c.available_orders)}</span>
											)}
											{Number.isFinite(Number(c.backpack_assigned)) && (
												<span>zaino {Number(c.backpack_assigned)}</span>
											)}
										</div>
									))}
								</div>
							</div>
						)}

							{selectedCell && selectedCell.riderId !== "free" && (
								<div className="dp2-slot-orders">
									<div className="dp2-slot-orders-title">Percorso del rider</div>
									{slotOrders.length === 0 ? (
										<div className="dp2-slot-orders-empty">Nessun ordine</div>
									) : (
										<div className="dp2-slot-orders-list">
											{(() => {
												const movableRowsCount = slotOrders.filter((row) => row && !row.isShop).length;
												return slotOrders.map((o, idx) => (
													<div
														className={`dp2-slot-order-row ${o.isCurrent ? "current" : ""} ${o.isShop ? "shop" : ""} ${!o.isShop && movableRowsCount > 1 ? "draggable" : ""} ${dragOverIndex === idx ? "drop-target" : ""}`}
														key={`slot-order-${o.id}-${o.isCurrent ? "current" : "row"}`}
														draggable={!o.isShop && movableRowsCount > 1}
														onDragStart={(e) => {
															if (o.isShop || movableRowsCount <= 1) return;
															if (e.dataTransfer) {
																e.dataTransfer.setData("text/plain", String(idx));
																e.dataTransfer.effectAllowed = "move";
															}
															setDragIndex(idx);
															setDragOverIndex(idx);
														}}
														onDragOver={(e) => {
															e.preventDefault();
															if (dragOverIndex !== idx) setDragOverIndex(idx);
														}}
														onDragLeave={() => {
															if (dragOverIndex === idx) setDragOverIndex(null);
														}}
														onDrop={(e) => {
															e.preventDefault();
															if (dragIndex === null) return;
															moveSlotOrder(dragIndex, idx);
															setDragIndex(null);
															setDragOverIndex(null);
														}}
														onDragEnd={() => {
															setDragIndex(null);
															setDragOverIndex(null);
														}}
													>
														<div className="dp2-slot-order-id">
															{!o.isShop && <span className="dp2-drag-grip">&#8942;&#8942;</span>}
															{o.isShop
																? `NEGOZIO ${o.address || ""}`.trim()
																: o.isCurrent
																	? "#ordine"
																	: `#${o.id}`}
															{o.isCurrent ? " (in corso)" : ""}
														</div>
														{!o.isShop && (
															<div className="dp2-slot-order-address">
																{o.address || "Indirizzo non disponibile"}
																{o.city ? `, ${o.city}` : ""}
															</div>
														)}
													</div>
												));
											})()}
										</div>
									)}
								</div>
							)}
					</div>
				</div>
			</div>

			<div className="dp2-sections">
				{noData || dataView.sections.length === 0 ? (
					renderEmptySection(loadError || "Nessun orario configurato")
				) : (
					dataView.sections.map((section) => renderSection(section))
				)}
			</div>

			<div className="dp2-actions">
				<button className="button-light" onClick={() => setPopup(false)}>
					Annulla
				</button>
				<button className="button-dark" disabled={!selectedCell} onClick={saveSelection}>
					Salva
				</button>
			</div>
		</div>
	);
}

function emptyView() {
	return {
		sections: [],
	};
}

function buildViewFromApi(data, options = {}) {
	const takeawayOnly = Boolean(options?.takeawayOnly);
	const ridersById = new Map();
	(data.riders || []).forEach((r) => {
		ridersById.set(`r${r.id}`, { id: `r${r.id}`, name: formatRiderName(r.name) });
	});
	ridersById.set("free", { id: "free", name: "Asporto" });

	const sections = (data.sections || []).map((section) => {
		const slotItems = [];
		const riderOrder = [];
		const cells = {};

		(section.slots || []).forEach((slot) => {
			const slotLabel = normalizeSlotLabel(slot?.slot_label, slot?.start);
			const disableReason = buildSlotDisableReason(slot);
				slotItems.push({
					label: slotLabel,
					dateTime: slot.start,
					isPast: isSlotInPast(slot),
					enabled: Number(slot.enabled) === 1,
					availableDrivers: Math.max(0, Number(slot.available_drivers_slot || 0)),
					warn: isSlotNearCapacity(slot),
				disableReason,
				statusLabels: buildSlotStatusLabels(slot, { takeawayOnly }),
			});
			(slot.cells || []).forEach((cell) => {
				const riderKey = cell.rider_id ? `r${cell.rider_id}` : "free";
				const rider = ridersById.get(riderKey) || {
					id: riderKey,
					name: formatRiderName(cell.rider_name || "Rider"),
				};
				ridersById.set(riderKey, rider);
				if (!riderOrder.includes(riderKey)) riderOrder.push(riderKey);
				const isFreeCell = !cell.rider_id;
				const enabledForFree =
					isFreeCell && !isSlotLocked(slot) && !isSlotOvenSaturated(slot) && !isSlotInPast(slot);
				cells[`${riderKey}-${slotLabel}`] = {
					driverId: cell.rider_id ? Number(cell.rider_id) : null,
					load: Number(cell.load || 0),
					backpack: Number(cell.backpack || 0),
					oven: Number(cell.oven || 0),
					enabled: enabledForFree || Number(cell.enabled) === 1,
					availableOrders:
						cell.available_orders === null || typeof cell.available_orders === "undefined"
							? null
							: Number(cell.available_orders),
					maxOrdersPerDriverSlot:
						cell.max_orders_per_driver_slot === null ||
							typeof cell.max_orders_per_driver_slot === "undefined"
							? null
							: Number(cell.max_orders_per_driver_slot),
					disabledReason: buildCellDisableReason(cell, slot),
					timeRange:
						Number(slot.delivery_time_range) > 0
							? Number(slot.delivery_time_range)
							: rangeToMinutes(slot.time_range || slotToRange(slotLabel)),
					dateTime: slot.start,
					freeNearCapacity:
						!cell.rider_id && Number(slot.max_load_slot || slot.max_load || 0) > 0
							? Number(cell.load || 0) <=
							Math.max(1, Math.ceil(Number(slot.max_load_slot || slot.max_load || 0) * 0.3))
							: false,
				};
			});
		});

		const riders = riderOrder.map((rk) => ridersById.get(rk)).filter(Boolean);
		riders.sort((a, b) => {
			if (a?.id === "free" && b?.id !== "free") return -1;
			if (b?.id === "free" && a?.id !== "free") return 1;
			return 0;
		});

		return {
			key: `service-${section.service}`,
			title: section.label || `Servizio ${section.service}`,
			slotItems,
			riders,
			cells,
		};
	});

	return {
		sections,
	};
}

function findCellForOrder(sections, targetDate, targetDriverId) {
	for (const section of sections || []) {
		const sectionKey = `service-${section.service}`;
		for (const slot of section.slots || []) {
			const slotLabel = normalizeSlotLabel(slot?.slot_label, slot?.start);
			for (const cell of slot.cells || []) {
				const cellDriverId = cell.rider_id ? Number(cell.rider_id) : null;
				if ((targetDriverId || null) !== (cellDriverId || null)) continue;

				const cellDate = moment(slot.start);
				if (!cellDate.isValid()) continue;
				if (!cellDate.isSame(targetDate, "minute")) continue;

				return {
					sectionKey,
					riderId: cell.rider_id ? `r${cell.rider_id}` : "free",
					driverId: cellDriverId,
					enabled: Number(cell.enabled) === 1,
					riderName: cell.rider_name
						? formatRiderName(cell.rider_name)
						: cellDriverId
							? `Rider ${cellDriverId}`
							: "Asporto",
					slot: slotLabel,
					dateTime: slot.start,
					timeRange:
						Number(slot.delivery_time_range) > 0
							? Number(slot.delivery_time_range)
							: rangeToMinutes(slot.time_range || slotToRange(slotLabel)),
					load: Number(cell.load || 0),
					backpack: Number(cell.backpack || 0),
					oven: Number(cell.oven || 0),
				};
			}
		}
	}
	return null;
}

function formatRiderName(value) {
	const full = String(value || "").trim();
	if (!full) return "";
	if (full === "Asporto" || full.startsWith("Rider ")) return full;
	const parts = full.split(/\s+/).filter(Boolean);
	if (parts.length < 2) return full;
	const surname = parts.slice(0, -1).join(" ");
	const name = parts[parts.length - 1];
	return `${name.charAt(0).toUpperCase()}. ${surname}`;
}

function slotToRange(slot) {
	const [h, m] = String(slot || "")
		.replace(".", ":")
		.split(":")
		.map((v) => Number(v));
	const start = moment().hour(h).minute(m).second(0);
	const end = start.clone().add(15, "minutes");
	return `${start.format("HH:mm")}-${end.format("HH:mm")}`;
}

function rangeToMinutes(range) {
	const parts = String(range || "").split("-");
	if (parts.length !== 2) return 15;
	const [h1, m1] = parts[0].split(":").map((v) => Number(v));
	const [h2, m2] = parts[1].split(":").map((v) => Number(v));
	if (![h1, m1, h2, m2].every((v) => Number.isFinite(v))) return 15;
	const start = h1 * 60 + m1;
	const end = h2 * 60 + m2;
	const diff = end - start;
	return diff > 0 ? diff : 15;
}

function buildSlotDisableReason(slot) {
	if (!slot || Number(slot.enabled) === 1) return "";
	if (isSlotInPast(slot)) return "Orario passato";

	const ovenReason = getSlotOvenDisableReason(slot);
	if (ovenReason) return ovenReason;

	const actualDrivers = Number(slot.actual_load_drivers || 0);
	const maxDrivers = Number(slot.max_load_drivers || 0);
	const incomingDriver = Number(slot.incoming_driver_load || 0);
	if (incomingDriver > 0 && actualDrivers + incomingDriver > maxDrivers) {
		return `Limite ordini driver raggiunto (${actualDrivers}/${maxDrivers})`;
	}

	const actualBackpack = Number(slot.actual_backpack_slot || 0);
	const maxBackpack = Number(slot.max_backpack_slot || 0);
	const incomingBackpack = Number(slot.incoming_backpack_load || 0);
	if (actualBackpack + incomingBackpack > maxBackpack) {
		return `Capacita zaino slot esaurita (${actualBackpack}/${maxBackpack})`;
	}

	if (Number(slot.locked) === 1) return "Slot bloccato";
	return "Slot non disponibile";
}

function buildSlotStatusLabels(slot, options = {}) {
	if (!slot || Number(slot.enabled) === 1) return [];
	const takeawayOnly = Boolean(options?.takeawayOnly);

	const labels = [];
	const actualLoad = Number(slot.actual_load || 0);
	const maxLoad = Number(slot.max_load || 0);
	const incomingLoad = Number(slot.incoming_load || 0);
	const actualSlotLoad = Number(slot.actual_load_slot || 0);
	const maxSlotLoad = Number(slot.max_load_slot || 0);
	const ovenExceeded = actualLoad + incomingLoad > maxLoad || actualSlotLoad + incomingLoad > maxSlotLoad;
	if (ovenExceeded) labels.push("Forno esaurito");

	const actualBackpack = Number(slot.actual_backpack_slot || 0);
	const maxBackpack = Number(slot.max_backpack_slot || 0);
	const incomingBackpack = Number(slot.incoming_backpack_load || 0);
	if (!takeawayOnly && actualBackpack + incomingBackpack > maxBackpack) labels.push("Zaino pieno");

	const actualDrivers = Number(slot.actual_load_drivers || 0);
	const maxDrivers = Number(slot.max_load_drivers || 0);
	const incomingDriver = Number(slot.incoming_driver_load || 0);
	if (!takeawayOnly && incomingDriver > 0 && actualDrivers + incomingDriver > maxDrivers) labels.push("Driver saturi");

	if (Number(slot.locked) === 1) labels.push("Slot bloccato");
	if (labels.length === 0) labels.push("Non disponibile");
	return labels;
}

function squashAlertLabel(label) {
	const text = String(label || "").trim();
	if (text.toLowerCase() === "non disponibile") return "Non disponibile";
	const firstWord = text.split(/\s+/)[0] || "";
	return firstWord;
}

function isSlotNearCapacity(slot) {
	if (!slot || Number(slot.enabled) !== 1) return false;

	const isNear = (actual, max, incoming = 0) => {
		const a = Number(actual || 0);
		const m = Number(max || 0);
		const i = Number(incoming || 0);
		if (!Number.isFinite(m) || m <= 0) return false;
		const remaining = m - (a + i);
		if (remaining < 0) return false;
		// Warn when slot reaches >=70% usage (remaining <=30%).
		const threshold = Math.max(1, Math.ceil(m * 0.3));
		return remaining <= threshold;
	};

	const nearOvenHour = isNear(slot.actual_load, slot.max_load, slot.incoming_load);
	const nearOvenSlot = isNear(slot.actual_load_slot, slot.max_load_slot, slot.incoming_load);
	const nearDrivers = isNear(slot.actual_load_drivers, slot.max_load_drivers, slot.incoming_driver_load);
	const nearBackpack = isNear(slot.actual_backpack_slot, slot.max_backpack_slot, slot.incoming_backpack_load);

	return nearOvenHour || nearOvenSlot || nearDrivers || nearBackpack;
}

function buildCellDisableReason(cell, slot) {
	const isFreeCell = !cell?.rider_id;
	if (isFreeCell) {
		if (isSlotInPast(slot)) return "Orario passato";
		const ovenReason = getSlotOvenDisableReason(slot);
		if (ovenReason) return ovenReason;
		if (isSlotLocked(slot)) return "Slot bloccato";
		return "";
	}

	const slotReason = buildSlotDisableReason(slot);
	if (slotReason) return slotReason;
	if (!cell) return "Cella non disponibile";
	const maxOrders =
		cell.max_orders_per_driver_slot === null || typeof cell.max_orders_per_driver_slot === "undefined"
			? null
			: Number(cell.max_orders_per_driver_slot);
	const availableOrders =
		cell.available_orders === null || typeof cell.available_orders === "undefined"
			? null
			: Number(cell.available_orders);
	if (
		Number.isFinite(maxOrders) &&
		Number.isFinite(availableOrders) &&
		availableOrders <= 0 &&
		maxOrders >= 0
	) {
		return `Limite ordini per rider raggiunto (${maxOrders}/${maxOrders})`;
	}
	return "Cella non disponibile";
}

function getSlotOvenDisableReason(slot) {
	const actualLoad = Number(slot?.actual_load || 0);
	const maxLoad = Number(slot?.max_load || 0);
	const incomingLoad = Number(slot?.incoming_load || 0);
	if (actualLoad + incomingLoad > maxLoad) {
		return `Capacita oraria esaurita (${actualLoad}/${maxLoad})`;
	}

	const actualSlotLoad = Number(slot?.actual_load_slot || 0);
	const maxSlotLoad = Number(slot?.max_load_slot || 0);
	if (actualSlotLoad + incomingLoad > maxSlotLoad) {
		return `Capacita slot esaurita (${actualSlotLoad}/${maxSlotLoad})`;
	}

	return "";
}

function isSlotOvenSaturated(slot) {
	return Boolean(getSlotOvenDisableReason(slot));
}

function isSlotLocked(slot) {
	return Number(slot?.locked) === 1;
}

function isSlotInPast(slot) {
	if (!slot?.start) return false;
	const start = moment(slot.start);
	if (!start.isValid()) return false;
	return start.isBefore(moment(), "minute");
}

function normalizeSlotLabel(rawLabel, start) {
	const normalized = String(rawLabel || "").trim().replace(".", ":");
	if (normalized) return normalized;
	const ts = moment(start);
	return ts.isValid() ? ts.format("HH:mm") : "";
}

function buildNoDataMessage(data, { orderDate, activeService, deliveryType }) {
	const serviceLabel = Number(activeService) === 2 ? "cena" : "pranzo";
	const dateLabel = moment(orderDate, "YYYY-MM-DD").isValid()
		? moment(orderDate, "YYYY-MM-DD").format("DD/MM/YYYY")
		: String(orderDate || "");
	if (!data) return `Nessun dato disponibile per ${serviceLabel} del ${dateLabel}`;

	if (deliveryType === "delivery" && !data?.meta?.polygon_id) {
		return "Indirizzo non coperto: nessun poligono attivo trovato per la consegna";
	}

	const sections = Array.isArray(data.sections) ? data.sections : [];
	if (sections.length === 0) {
		return `Nessun orario configurato per ${serviceLabel} del ${dateLabel}`;
	}

	const hasSlots = sections.some((s) => Array.isArray(s.slots) && s.slots.length > 0);
	if (!hasSlots) {
		return `Nessuno slot disponibile per ${serviceLabel} del ${dateLabel}`;
	}

	return `Nessun dato disponibile per ${serviceLabel} del ${dateLabel}`;
}

function computeCartLoadInfo(cart) {
	const products = Array.isArray(cart?.products) ? cart.products : [];
	if (products.length === 0) return { backpack: 0, oven: 0 };

	let oven = 0;
	let backpack = 0;
	for (const p of products) {
		const qty = Number(p.qty) || 0;
		if (qty <= 0) continue;
		const size = Number(p.size) || 0;
		const driverSize = Number(p.driver_size) || 0;
		oven += qty * size;
		backpack += qty * driverSize;
	}

	return {
		backpack,
		oven,
	};
}

function DeliveryMap({
	shopCoord,
	riders,
	orders,
	currentOrderCoord,
	selectedSlotOrders,
	sections,
	slotMinutes,
	selectedSlotStart,
	selectedDriverId,
	onOrderSelect,
}) {
	const mapElRef = useRef(null);
	const mapRef = useRef(null);
	const layerRef = useRef(null);
	const fitPointsRef = useRef([]);
	const fitFnRef = useRef(null);
	const [mapError, setMapError] = useState(false);

	useEffect(() => {
		let mounted = true;

		async function renderMap() {
			let L = null;
			try {
				L = await ensureLeaflet();
				setMapError(false);
			} catch (error) {
				setMapError(true);
				return;
			}
			if (!mounted || !mapElRef.current) return;

			if (!mapRef.current) {
				mapRef.current = L.map(mapElRef.current, {
					zoomControl: true,
					attributionControl: true,
				});
				L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
					maxZoom: 19,
					attribution: "&copy; OpenStreetMap",
					className: "map-tiles-grey",
				}).addTo(mapRef.current);
				layerRef.current = L.layerGroup().addTo(mapRef.current);
			}
			fitFnRef.current = (points) => {
				if (!mapRef.current || !Array.isArray(points) || points.length === 0) return;
				mapRef.current.invalidateSize();
				if (points.length >= 2) {
					mapRef.current.fitBounds(L.latLngBounds(points), { padding: [24, 24] });
				} else {
					mapRef.current.setView(points[0], 14);
				}
			};

			const map = mapRef.current;
			const layer = layerRef.current;
			// Popup content can change size after mount; force recalculation.
			map.invalidateSize();
			layer.clearLayers();

			const shop = parseCoord(shopCoord);
			const currentOrder = parseCoord(currentOrderCoord);
			const ordersCoordsRaw = (orders || [])
				.map((o) => ({ ...o, parsed: parseCoord(o.coord) }))
				.filter((o) => o.parsed);
			const ordersCoords = ordersCoordsRaw.map((o) => {
				const selection = findCellForOrder(
					sections || [],
					moment(o.delivery_date, "YYYY-MM-DD HH:mm:ss"),
					Number(o.driver_id || 0) || null,
				);
				return {
					...o,
					selection,
					isAvailable: Boolean(selection?.enabled),
				};
			});
			const slotRanges = buildSlotRanges(sections, slotMinutes);
			const hasSelectedSlot = Boolean(selectedSlotStart);
			const hasDriverFilter =
				hasSelectedSlot &&
				selectedDriverId !== "all" &&
				selectedDriverId !== null &&
				typeof selectedDriverId !== "undefined";
			const selectedDriverNorm = hasDriverFilter ? Number(selectedDriverId || 0) : null;
			const isFreeSelection = hasDriverFilter && selectedDriverNorm === 0;
			const visibleOrders = selectedSlotStart
				? ordersCoords.filter((o) => {
					const slot = getOrderSlot(o.delivery_date, slotRanges, slotMinutes);
					if (slot?.start !== selectedSlotStart) return false;
					if (!hasDriverFilter) return true;
					if (isFreeSelection) return false;
					return Number(o.driver_id || 0) === selectedDriverNorm;
				})
				: ordersCoords;
			const ridersCoords = (riders || [])
				.map((r) => ({ ...r, parsed: parseCoord(r.coord) }))
				.filter((r) => r.parsed);

			if (shop) {
				L.circleMarker([shop.lat, shop.lng], {
					radius: 9,
					weight: 2,
					color: "#1d3557",
					fillColor: "#3a86ff",
					fillOpacity: 0.95,
				})
					.bindTooltip("Negozio")
					.addTo(layer);
			}

			if (!isFreeSelection) {
				for (const r of ridersCoords) {
					L.circleMarker([r.parsed.lat, r.parsed.lng], {
						radius: 7,
						weight: 2,
						color: "#7f5539",
						fillColor: "#ffb703",
						fillOpacity: 0.95,
					})
						.bindTooltip(r.name || `Rider ${r.id}`)
						.addTo(layer);
				}

				for (const o of visibleOrders) {
					L.circleMarker([o.parsed.lat, o.parsed.lng], {
						radius: 7,
						weight: 2,
						color: o.isAvailable ? "#2d6a4f" : "#9d0208",
						fillColor: o.isAvailable ? "#38b000" : "#ef233c",
						fillOpacity: 0.95,
					})
						.bindTooltip(`Ordine ${o.id} - ${o.isAvailable ? "Disponibile" : "Non disponibile"}`)
						.on("click", () => {
							if (o.isAvailable && o.selection && typeof onOrderSelect === "function") {
								onOrderSelect(o.selection);
							}
						})
						.addTo(layer);
				}
			}

			// With a selected slot+rider, draw a single route using the right-side list order.
			if (hasSelectedSlot && hasDriverFilter && !isFreeSelection) {
				const ordersById = new Map(ordersCoords.map((o) => [Number(o.id), o]));
				const orderedStops = [];
				const rows = Array.isArray(selectedSlotOrders) ? selectedSlotOrders : [];

				for (const row of rows) {
					if (!row || row.isShop) continue;
					if (row.isCurrent) {
						if (currentOrder) orderedStops.push({ parsed: { lat: currentOrder.lat, lng: currentOrder.lng } });
						continue;
					}
					const oid = Number(row.id || 0);
					const match = ordersById.get(oid);
					if (match?.parsed) orderedStops.push(match);
				}

				const points = [];
				if (shop) points.push([shop.lat, shop.lng]);
				for (const stop of orderedStops) points.push([stop.parsed.lat, stop.parsed.lng]);

				if (points.length >= 2) {
					const color = colorForRoute(`slot-${selectedSlotStart}-single`);
					L.polyline(points, {
						color,
						weight: 3,
						opacity: 0.85,
					})
						.bindTooltip("Percorso slot selezionato")
						.addTo(layer);
					addDirectionArrowMarkers(L, layer, points, color);

					if (shop && orderedStops.length > 1) {
						const last = orderedStops[orderedStops.length - 1];
						const returnPoints = [
							[last.parsed.lat, last.parsed.lng],
							[shop.lat, shop.lng],
						];
						L.polyline(returnPoints, {
							color,
							weight: 3,
							opacity: 0.7,
							dashArray: "6, 6",
						})
							.bindTooltip("Rientro negozio")
							.addTo(layer);
						addDirectionArrowMarkers(L, layer, returnPoints, color);
					}
				}
			} else {
				const routeOrders = ordersCoords.filter((o) => {
					const did = Number(o.driver_id || 0);
					if (hasDriverFilter && selectedDriverNorm === 0) return did === 0;
					if (hasDriverFilter) return did === selectedDriverNorm;
					return did > 0;
				});
				const groupedRoutes = new Map();
				for (const o of routeOrders) {
					const slot = getOrderSlot(o.delivery_date, slotRanges, slotMinutes);
					if (selectedSlotStart && (!slot || slot.start !== selectedSlotStart)) continue;
					const slotKey = slot ? slot.start : `na-${moment(o.delivery_date).format("YYYY-MM-DD HH:mm")}`;
					const driverNorm = Number(o.driver_id || 0);
					const routeKey = `${slotKey}__${driverNorm}`;
					if (!groupedRoutes.has(routeKey)) {
						groupedRoutes.set(routeKey, {
							routeKey,
							driverId: driverNorm,
							slotLabel: slot ? slot.label : "N/D",
							orders: [],
						});
					}
					groupedRoutes.get(routeKey).orders.push(o);
				}

				// Ensure selected route exists even if there are no existing orders for that rider/slot.
				if (hasSelectedSlot && hasDriverFilter && currentOrder) {
					const selectedRouteKey = `${selectedSlotStart}__${selectedDriverNorm}`;
					if (!groupedRoutes.has(selectedRouteKey)) {
						groupedRoutes.set(selectedRouteKey, {
							routeKey: selectedRouteKey,
							driverId: selectedDriverNorm,
							slotLabel: moment(selectedSlotStart).format("HH.mm"),
							orders: [],
						});
					}
				}

				for (const route of groupedRoutes.values()) {
					const rider = riders.find((r) => Number(r.id) === route.driverId);
					const riderLabel = route.driverId > 0 ? rider?.name || `Rider ${route.driverId}` : "Asporto";
					route.orders.sort((a, b) => {
						const ao = Number.isFinite(Number(a.delivery_order)) ? Number(a.delivery_order) : 9999;
						const bo = Number.isFinite(Number(b.delivery_order)) ? Number(b.delivery_order) : 9999;
						if (ao !== bo) return ao - bo;
						return moment(a.delivery_date).valueOf() - moment(b.delivery_date).valueOf();
					});
					let orderedRouteOrders = [...route.orders];
					// With selected slot+driver, respect the manual ordering list and include current order in the same route.
					if (
						hasSelectedSlot &&
						hasDriverFilter &&
						Number(route.driverId || 0) === selectedDriverNorm &&
						Array.isArray(selectedSlotOrders)
					) {
						const byId = new Map(route.orders.map((o) => [Number(o.id), o]));
						const custom = [];
						for (const row of selectedSlotOrders) {
							if (!row || row.isShop) continue;
							if (row.isCurrent) {
								if (currentOrder) {
									custom.push({
										id: "current",
										delivery_order: Number(row.delivery_order || 9999),
										parsed: { lat: currentOrder.lat, lng: currentOrder.lng },
									});
								}
								continue;
							}
							const oid = Number(row.id || 0);
							const match = byId.get(oid);
							if (match && Number(match.driver_id || 0) === selectedDriverNorm) custom.push(match);
						}
						if (custom.length > 0) orderedRouteOrders = custom;
					}

					const points = [];
					if (shop) points.push([shop.lat, shop.lng]);
					for (const o of orderedRouteOrders) points.push([o.parsed.lat, o.parsed.lng]);
					if (points.length < 2) continue;

					const color = colorForRoute(route.routeKey || `${route.slotLabel}-${route.driverId}`);
					L.polyline(points, {
						color,
						weight: 3,
						opacity: 0.8,
					})
						.bindTooltip(`${riderLabel} - ${route.slotLabel}`)
						.addTo(layer);
					addDirectionArrowMarkers(L, layer, points, color);

					// If route has more than one order, draw explicit return leg to shop.
					if (shop && orderedRouteOrders.length > 1) {
						const last = orderedRouteOrders[orderedRouteOrders.length - 1];
						const returnPoints = [
							[last.parsed.lat, last.parsed.lng],
							[shop.lat, shop.lng],
						];
						L.polyline(
							returnPoints,
							{
								color,
								weight: 3,
								opacity: 0.7,
								dashArray: "6, 6",
							},
						)
							.bindTooltip(`${riderLabel} - Rientro negozio`)
							.addTo(layer);
						addDirectionArrowMarkers(L, layer, returnPoints, color);
					}
				}
			}

			if (currentOrder && !isFreeSelection) {
				L.marker([currentOrder.lat, currentOrder.lng], {
					icon: L.divIcon({
						className: "dp2-current-order-icon",
						html: '<span class="dp2-current-order-dot"></span>',
						iconSize: [28, 28],
						iconAnchor: [14, 14],
					}),
				})
					.bindTooltip("Ordine corrente")
					.addTo(layer);
			}

			// Requested behavior: pan/fit on shop + orders.
			const fitPoints = [];
			if (shop) fitPoints.push([shop.lat, shop.lng]);
			for (const o of visibleOrders) fitPoints.push([o.parsed.lat, o.parsed.lng]);
			if (currentOrder && !isFreeSelection) fitPoints.push([currentOrder.lat, currentOrder.lng]);
			fitPointsRef.current = fitPoints;

			if (fitPoints.length >= 1 && fitFnRef.current) {
				fitFnRef.current(fitPoints);
			} else {
				map.setView([45.4642, 9.19], 11);
			}
			setTimeout(() => {
				if (mounted && mapRef.current) mapRef.current.invalidateSize();
			}, 0);
		}

		renderMap();
		return () => {
			mounted = false;
		};
	}, [
		shopCoord,
		riders,
		orders,
		currentOrderCoord,
		selectedSlotOrders,
		sections,
		slotMinutes,
		selectedSlotStart,
		selectedDriverId,
		onOrderSelect,
	]);

	useEffect(() => {
		let resizeTimer = null;
		const handleResize = () => {
			if (resizeTimer) clearTimeout(resizeTimer);
			resizeTimer = setTimeout(() => {
				const points = fitPointsRef.current || [];
				if (fitFnRef.current && points.length > 0) fitFnRef.current(points);
				else if (mapRef.current) mapRef.current.invalidateSize();
			}, 80);
		};

		let observer = null;
		if (typeof ResizeObserver !== "undefined") {
			observer = new ResizeObserver(handleResize);
			if (mapElRef.current?.parentElement) observer.observe(mapElRef.current.parentElement);
		}
		window.addEventListener("resize", handleResize);

		return () => {
			if (observer) observer.disconnect();
			window.removeEventListener("resize", handleResize);
			if (resizeTimer) clearTimeout(resizeTimer);
			if (mapRef.current) {
				mapRef.current.remove();
				mapRef.current = null;
				layerRef.current = null;
			}
		};
	}, []);

	if (mapError) {
		return <div className="dp2-map-fallback">Mappa non disponibile</div>;
	}

	return <div className="dp2-map-root" ref={mapElRef} />;
}

function buildSlotRanges(sections, slotMinutes) {
	const ranges = [];
	for (const section of sections || []) {
		for (const slot of section.slots || []) {
			if (!slot?.start) continue;
			const start = moment(slot.start);
			const end = slot.end ? moment(slot.end) : start.clone().add(Number(slotMinutes || 15), "minutes");
			ranges.push({
				start: start.format("YYYY-MM-DD HH:mm:ss"),
				label: slot.slot_label || start.format("HH.mm"),
				startMs: start.valueOf(),
				endMs: end.valueOf(),
			});
		}
	}
	return ranges;
}

function getOrderSlot(orderDate, slotRanges, slotMinutes) {
	const ts = moment(orderDate).valueOf();
	if (!Number.isFinite(ts)) return null;
	for (const s of slotRanges || []) {
		if (ts >= s.startMs && ts < s.endMs) return s;
	}
	const rounded = moment(orderDate)
		.startOf("minute")
		.subtract(moment(orderDate).minute() % Number(slotMinutes || 15), "minutes");
	return {
		start: rounded.format("YYYY-MM-DD HH:mm:ss"),
		label: rounded.format("HH.mm"),
		startMs: rounded.valueOf(),
		endMs: rounded.clone().add(Number(slotMinutes || 15), "minutes").valueOf(),
	};
}

function colorForRider(riderId) {
	const palette = ["#1d3557", "#e63946", "#2a9d8f", "#6a4c93", "#f4a261", "#1982c4", "#8ac926", "#ff595e"];
	const id = Number(riderId) || 0;
	return palette[Math.abs(id) % palette.length];
}

function colorForRoute(routeKey) {
	const palette = [
		"#1d3557",
		"#e63946",
		"#2a9d8f",
		"#6a4c93",
		"#f4a261",
		"#1982c4",
		"#8ac926",
		"#ff595e",
		"#8338ec",
		"#fb8500",
	];
	const key = String(routeKey || "");
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
	}
	return palette[hash % palette.length];
}

function addDirectionArrowMarkers(L, layer, points, color) {
	if (!Array.isArray(points) || points.length < 2) return;
	for (let i = 0; i < points.length - 1; i++) {
		const from = points[i];
		const to = points[i + 1];
		if (!from || !to) continue;
		const angle = computeBearingDeg(from[0], from[1], to[0], to[1]);
		const mid = [(from[0] + to[0]) / 2, (from[1] + to[1]) / 2];
		L.marker(mid, {
			interactive: false,
			icon: L.divIcon({
				className: "dp2-direction-arrow",
				html: `<span style="display:block;color:${color};font-size:16px;line-height:1;transform: rotate(${angle}deg);">&#9650;</span>`,
				iconSize: [16, 16],
				iconAnchor: [8, 8],
			}),
		}).addTo(layer);
	}
}

function computeBearingDeg(lat1, lng1, lat2, lng2) {
	const toRad = (v) => (v * Math.PI) / 180;
	const toDeg = (v) => (v * 180) / Math.PI;
	const y = Math.sin(toRad(lng2 - lng1)) * Math.cos(toRad(lat2));
	const x =
		Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
		Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lng2 - lng1));
	const brng = toDeg(Math.atan2(y, x));
	return (brng + 360) % 360;
}

function parseCoord(coord) {
	if (!coord) return null;
	if (typeof coord === "object" && coord !== null) {
		const lat = Number(coord.lat ?? coord.latitude);
		const lng = Number(coord.lng ?? coord.lon ?? coord.longitude);
		if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
		if (typeof coord.coord === "string") return parseCoord(coord.coord);
		return null;
	}
	const parts = String(coord).split(",");
	if (parts.length !== 2) return null;
	const lat = Number(parts[0].trim());
	const lng = Number(parts[1].trim());
	if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
	return { lat, lng };
}

async function ensureLeaflet() {
	if (window.L) return window.L;
	if (window.__leafletLoadingPromise) return window.__leafletLoadingPromise;

	window.__leafletLoadingPromise = new Promise((resolve, reject) => {
		const cssId = "leaflet-css-cdn";
		if (!document.getElementById(cssId)) {
			const link = document.createElement("link");
			link.id = cssId;
			link.rel = "stylesheet";
			link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
			document.head.appendChild(link);
		}

		const existing = document.getElementById("leaflet-js-cdn");
		if (existing) {
			existing.addEventListener("load", () => resolve(window.L));
			existing.addEventListener("error", reject);
			return;
		}

		const script = document.createElement("script");
		script.id = "leaflet-js-cdn";
		script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
		script.async = true;
		script.onload = () => resolve(window.L);
		script.onerror = reject;
		document.body.appendChild(script);
	});

	return window.__leafletLoadingPromise;
}

export default DatePopUpV2;
